import React, { useState, useEffect, useCallback } from 'react';

const OnboardingGuide = ({ steps, storageKey = 'onboarding-completed', onComplete, theme = 'eleve' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // Couleurs selon le thème - VERT
  const colors = theme === 'prof' 
    ? { primary: '#065f46', secondary: '#047857' }  // Émeraude pour prof
    : { primary: '#10b981', secondary: '#059669' }; // Vert clair pour élève

  const highlightTarget = useCallback((targetSelector) => {
    const element = document.querySelector(targetSelector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      
      setTimeout(() => {
        const newRect = element.getBoundingClientRect();
        setTargetRect(newRect);
      }, 500);
      
      return true;
    } else {
      setTargetRect(null);
      return false;
    }
  }, []);

  useEffect(() => {
    const completed = localStorage.getItem(storageKey);
    if (completed || !steps || steps.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      const found = highlightTarget(steps[0].target);
      if (found) {
        setIsVisible(true);
        setIsReady(true);
      } else {
        setTimeout(() => {
          highlightTarget(steps[0].target);
          setIsVisible(true);
          setIsReady(true);
        }, 1000);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [steps, storageKey, highlightTarget]);

  useEffect(() => {
    if (!isVisible || !steps[currentStep]) return;

    const updatePosition = () => {
      highlightTarget(steps[currentStep].target);
    };

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, currentStep, steps, highlightTarget]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setTimeout(() => {
        highlightTarget(steps[nextStep].target);
      }, 100);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setTimeout(() => {
        highlightTarget(steps[prevStep].target);
      }, 100);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    localStorage.setItem(storageKey, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible || !isReady || !steps || steps.length === 0) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const padding = 8;
  const overlayColor = 'rgba(0, 0, 0, 0.80)';

  const getBubblePosition = () => {
    const bubbleWidth = 300;
    const bubbleHeight = 180;
    const offset = 15;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    if (!targetRect) {
      return { 
        position: 'fixed',
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)' 
      };
    }

    let style = { position: 'fixed' };
    const position = step.position || 'bottom';

    const spaceAbove = targetRect.top;
    const spaceBelow = viewportHeight - targetRect.bottom;
    const spaceLeft = targetRect.left;
    const spaceRight = viewportWidth - targetRect.right;

    let finalPosition = position;

    if (position === 'bottom' && spaceBelow < bubbleHeight + offset) {
      finalPosition = spaceAbove > spaceBelow ? 'top' : 'right';
    } else if (position === 'top' && spaceAbove < bubbleHeight + offset) {
      finalPosition = spaceBelow > spaceAbove ? 'bottom' : 'right';
    } else if (position === 'left' && spaceLeft < bubbleWidth + offset) {
      finalPosition = spaceRight > spaceLeft ? 'right' : 'bottom';
    } else if (position === 'right' && spaceRight < bubbleWidth + offset) {
      finalPosition = spaceLeft > spaceRight ? 'left' : 'bottom';
    }

    switch (finalPosition) {
      case 'top':
        style.top = `${Math.max(10, targetRect.top - bubbleHeight - offset - padding)}px`;
        style.left = `${Math.max(10, Math.min(targetRect.left + targetRect.width / 2 - bubbleWidth / 2, viewportWidth - bubbleWidth - 10))}px`;
        break;
      case 'bottom':
        style.top = `${Math.min(targetRect.bottom + offset + padding, viewportHeight - bubbleHeight - 10)}px`;
        style.left = `${Math.max(10, Math.min(targetRect.left + targetRect.width / 2 - bubbleWidth / 2, viewportWidth - bubbleWidth - 10))}px`;
        break;
      case 'left':
        style.top = `${Math.max(10, Math.min(targetRect.top - 50, viewportHeight - bubbleHeight - 10))}px`;
        style.left = `${Math.max(10, targetRect.left - bubbleWidth - offset - padding)}px`;
        break;
      case 'right':
        style.top = `${Math.max(10, Math.min(targetRect.top - 50, viewportHeight - bubbleHeight - 10))}px`;
        style.left = `${Math.min(targetRect.right + offset + padding, viewportWidth - bubbleWidth - 10)}px`;
        break;
      default:
        style.top = `${Math.min(targetRect.bottom + offset + padding, viewportHeight - bubbleHeight - 10)}px`;
        style.left = `${Math.max(10, Math.min(targetRect.left + targetRect.width / 2 - bubbleWidth / 2, viewportWidth - bubbleWidth - 10))}px`;
    }

    return style;
  };

  return (
    <>
      {/* 4 Overlays pour créer le trou */}
      {targetRect ? (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: `${targetRect.top - padding}px`,
            backgroundColor: overlayColor,
            zIndex: 10000
          }} onClick={handleSkip} />
          
          <div style={{
            position: 'fixed',
            top: `${targetRect.bottom + padding}px`,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: overlayColor,
            zIndex: 10000
          }} onClick={handleSkip} />
          
          <div style={{
            position: 'fixed',
            top: `${targetRect.top - padding}px`,
            left: 0,
            width: `${targetRect.left - padding}px`,
            height: `${targetRect.height + padding * 2}px`,
            backgroundColor: overlayColor,
            zIndex: 10000
          }} onClick={handleSkip} />
          
          <div style={{
            position: 'fixed',
            top: `${targetRect.top - padding}px`,
            left: `${targetRect.right + padding}px`,
            right: 0,
            height: `${targetRect.height + padding * 2}px`,
            backgroundColor: overlayColor,
            zIndex: 10000
          }} onClick={handleSkip} />

          <div style={{
            position: 'fixed',
            top: `${targetRect.top - padding}px`,
            left: `${targetRect.left - padding}px`,
            width: `${targetRect.width + padding * 2}px`,
            height: `${targetRect.height + padding * 2}px`,
            border: `3px solid ${colors.primary}`,
            borderRadius: '12px',
            zIndex: 10001,
            pointerEvents: 'none',
            animation: 'pulse 2s infinite'
          }} />
        </>
      ) : (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: overlayColor,
          zIndex: 10000
        }} onClick={handleSkip} />
      )}

      {/* Bulle d'aide */}
      <div
        style={{
          ...getBubblePosition(),
          width: '300px',
          backgroundColor: 'white',
          borderRadius: '14px',
          boxShadow: '0 15px 50px rgba(0, 0, 0, 0.3)',
          zIndex: 10002,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          padding: '14px 16px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ 
              fontSize: '12px', 
              opacity: 0.9,
              background: 'rgba(255,255,255,0.2)',
              padding: '3px 8px',
              borderRadius: '10px'
            }}>
              {currentStep + 1} / {steps.length}
            </span>
            <button
              onClick={handleSkip}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
          <h3 style={{ margin: '8px 0 0 0', fontSize: '17px', fontWeight: '600' }}>
            {step.icon} {step.title}
          </h3>
        </div>

        {/* Content */}
        <div style={{ padding: '14px 16px' }}>
          <p style={{ margin: 0, color: '#4a5568', lineHeight: '1.5', fontSize: '14px' }}>
            {step.content}
          </p>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#f9fafb'
        }}>
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
              fontSize: '13px',
              padding: '6px'
            }}
          >
            Passer
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                style={{
                  padding: '8px 14px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#374151'
                }}
              >
                ←
              </button>
            )}
            <button
              onClick={handleNext}
              style={{
                padding: '8px 18px',
                border: 'none',
                borderRadius: '8px',
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                color: 'white',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                boxShadow: `0 4px 12px ${colors.primary}66`
              }}
            >
              {isLastStep ? '✓ OK' : 'Suivant →'}
            </button>
          </div>
        </div>

        {/* Progress dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          paddingBottom: '12px',
          background: '#f9fafb'
        }}>
          {steps.map((_, index) => (
            <div
              key={index}
              style={{
                width: index === currentStep ? '20px' : '6px',
                height: '6px',
                borderRadius: '3px',
                backgroundColor: index === currentStep ? colors.primary : '#ddd',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { border-color: ${colors.primary}; box-shadow: 0 0 0 0 ${colors.primary}66; }
          50% { border-color: ${colors.secondary}; box-shadow: 0 0 15px 3px ${colors.primary}4D; }
        }
      `}</style>
    </>
  );
};

export default OnboardingGuide;
